import React, { useMemo, forwardRef, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import { FixedSizeList } from 'react-window';
import TextField from '@mui/material/TextField';
import { isNullOrEmpty, t } from '@cyberint/common-utils';
import { CustomTooltip } from '../Poppers';
import Icons, { IconSizes } from '../Icons';
import { Themes } from '../../style/theme-consts';
import { InputHeights } from '../../utils/consts';
import { DomUtils } from '../../utils/dom-utils';
import { RenderValueTypes } from './select-consts';
import { buildRenderer, mapSubOptions, spreadHierarchicalSelection } from './selectUtils';
import * as S from './styled';

// ---- Virtualized listbox (opt-in via `virtualize` prop) -------------------
// Standard MUI + react-window pattern: MUI still builds one React element per
// option (cheap), but only the visible rows are mounted to the DOM. Use for
// large option sets (hundreds+) where the full DOM list is the bottleneck.
const LISTBOX_PADDING = 8;
const VIRTUAL_ROW_HEIGHT = 36; // matches the real StyledMenuItem height (14px font + 6px vertical padding)
const VIRTUAL_MAX_ROWS = 8;
// Below this option count the native MUI listbox is cheap and keeps full
// keyboard navigation (react-window only mounts visible rows, which breaks
// arrow/Page nav past the viewport). Only window past the threshold, where
// the DOM-node cost dominates and users filter by typing anyway.
const VIRTUALIZE_THRESHOLD = 100;

const renderVirtualRow = ({ data, index, style }) =>
  React.cloneElement(data[index], { style: { ...style, top: style.top + LISTBOX_PADDING } });

const OuterElementContext = createContext({});
const OuterElement = forwardRef((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  // Lock horizontal scrolling: without this react-window's `overflow: auto` shows
  // a spurious horizontal scrollbar when an option is wider than the popper.
  return <div ref={ref} {...props} {...outerProps} style={{ ...props.style, overflowX: 'hidden' }} />;
});
OuterElement.displayName = 'AutocompleteOuterElement';

// Reset the default <ul> padding/margin so rows align flush-left (the browser
// default `padding-inline-start: 40px` both indents rows and forces overflow).
const InnerElement = forwardRef((props, ref) => (
  <ul ref={ref} {...props} style={{ ...props.style, padding: 0, margin: 0 }} />
));
InnerElement.displayName = 'AutocompleteInnerElement';

const VirtualizedListbox = forwardRef(({ children, ...other }, ref) => {
  const items = React.Children.toArray(children);
  const itemCount = items.length;
  const height = Math.min(itemCount, VIRTUAL_MAX_ROWS) * VIRTUAL_ROW_HEIGHT + 2 * LISTBOX_PADDING;

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          itemData={items}
          itemCount={itemCount}
          itemSize={VIRTUAL_ROW_HEIGHT}
          height={height}
          width="100%"
          outerElementType={OuterElement}
          innerElementType={InnerElement}
          overscanCount={5}>
          {renderVirtualRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});
VirtualizedListbox.displayName = 'VirtualizedListbox';
// ---------------------------------------------------------------------------

const Autocomplete = ({
  multiple,
  options,
  onChange,
  onBlur,
  onInputChange,
  onClose,
  onOpen,
  open,
  value,
  inputValue,
  label,
  width = '100%',
  height = InputHeights.MEDIUM,
  minHeight,
  maxPopperHeight,
  placeholder: origPlaceHolder,
  renderValue = RenderValueTypes.CHIPS,
  chipsCount = 1,
  mode: origMode,
  paperSx,
  disablePortal = true,
  isOptionEqualToValue,
  filterOptions,
  clearOnBlur,
  blurOnSelect,
  noOptionsText,
  renderInput,
  freeSolo,
  readOnly = false,
  loading,
  testId,
  clearable = true,
  disabled,
  error,
  helperText,
  autoFocus = false,
  virtualize = false
}) => {
  const { theme, elementRef } = DomUtils.useDetectParentTheme();
  const mode = origMode || theme || Themes.LIGHT;
  const placeholder = origPlaceHolder === true ? t(freeSolo ? 'common.selectOrAdd' : 'common.select') : origPlaceHolder;
  const hasHierarchy = useMemo(() => options.some(o => o.children?.length), [options]);
  const preparedOptions = useMemo(() => mapSubOptions(options), [options]);
  // Build the value->option lookup from the already-flattened list (single pass,
  // O(n)). Previously this re-flattened AND used object-spread-in-reduce → O(n²).
  const optionsByValue = useMemo(() => Object.fromEntries(preparedOptions.map(o => [o.value, o])), [preparedOptions]);
  // O(1) membership checks per option instead of Array.indexOf per option.
  const selectedSet = useMemo(() => new Set(Array.isArray(value) ? value : []), [value]);
  // Only window large lists — small lists keep the native, fully keyboard-navigable listbox.
  const shouldVirtualize = virtualize && preparedOptions.length > VIRTUALIZE_THRESHOLD;

  const onChangeHandler = (_, val) => {
    // `v.inputValue` covers the freeSolo "Add \"x\"" option (see getFilterOptions in
    // autocomplete-utils): it carries the typed string on `inputValue`, not `value`.
    const toValue = v => v?.value ?? v?.inputValue ?? v;
    const values = Array.isArray(val) ? val.map(toValue) : toValue(val);
    if (multiple && Array.isArray(values) && values.length > 0 && hasHierarchy) {
      const adapted = spreadHierarchicalSelection({ values, optionsByValue });
      onChange?.(adapted);
    } else {
      onChange?.(values);
    }
  };

  const clearSelectionHandler = e => {
    e.stopPropagation();
    onChange?.(multiple ? [] : null, { isClear: true });
  };

  const renderOptionFunc = (props, option) => {
    const selectedChildren = option.children?.filter(c => selectedSet.has(c.value))?.length;
    const isMultipleOptionChecked = () => {
      if (Array.isArray(value) && value.some(v => Array.isArray(v))) {
        // handling case if array of selected values (props "value") includes value as type "array" and not primitive("strings")
        return value.some(v => isEqual(v, option.value));
      }
      return selectedSet.has(option.value) || (selectedChildren ? selectedChildren === option.children?.length : false);
    };
    return (
      <S.StyledMenuItem
        {...props}
        key={option.key || (typeof option.label === 'string' ? option.label : option.value)}
        value={option.value}
        disabled={!!option.disabled}
        data-testid={`autocomplete-menu-item_${option.value}`}
        $sub={!!option.sub}>
        {multiple && (
          <S.StyledCheckbox
            checked={isMultipleOptionChecked()}
            indeterminate={
              selectedChildren ? selectedChildren < option.children?.length && selectedChildren > 0 : false
            }
            withPropagation
          />
        )}
        <S.StyledOption>
          {option.icon}
          {option.dotColor && <S.ColoredCircle $color={option.dotColor} />}
          {option.label}
          {!!option.tooltip && (
            <CustomTooltip title={option.tooltip} mode={mode} placement="right">
              <S.TooltipWrapper onClick={e => e.stopPropagation()}>
                <Icons.Info size={IconSizes.S} />
              </S.TooltipWrapper>
            </CustomTooltip>
          )}
        </S.StyledOption>
      </S.StyledMenuItem>
    );
  };

  const renderInputFunc = ({ inputProps, ...params }) => (
    <TextField
      {...params}
      inputProps={{
        ...inputProps,
        value: optionsByValue?.[inputProps.value]?.label || inputProps.value,
        readOnly
      }}
      size="small"
      variant="outlined"
      label={label}
      placeholder={placeholder}
      error={error}
      helperText={helperText}
      data-testid={testId}
      autoFocus={autoFocus}
    />
  );

  const renderer = useMemo(() => {
    const clearValue = v => {
      const filtered = value?.filter(sv => sv !== v);
      onChange?.(filtered, { isClear: true });
    };
    return buildRenderer({ renderValue, chipsCount, value, multiple, optionsByValue, testId, clearValue });
  }, [renderValue, chipsCount, optionsByValue, onChange, value, multiple]);

  const compProps = useMemo(
    () => ({
      paper: { classes: { root: mode }, sx: { ...S.POPPER_STYLE, ...paperSx } }
    }),
    [mode, paperSx]
  );

  return (
    <S.AutocompleteWrapper ref={origMode ? null : elementRef} $maxPopperHeight={maxPopperHeight}>
      <S.Autocomplete
        data-testid={testId}
        $width={width}
        $height={height}
        $minHeight={minHeight}
        multiple={multiple}
        disableCloseOnSelect={multiple}
        disablePortal={disablePortal}
        options={preparedOptions}
        disabled={disabled}
        value={value}
        popupIcon={<S.StyledExpandMore $hasSelectedValue={!isNullOrEmpty(value)} />}
        clearIcon={clearable && <S.Clear onClick={clearSelectionHandler} />}
        getOptionLabel={option => option?.label || option}
        isOptionEqualToValue={isOptionEqualToValue ?? ((option, val) => option.value === (val?.value ?? val))}
        onChange={onChangeHandler}
        onInputChange={onInputChange}
        inputValue={inputValue}
        onBlur={onBlur}
        onClose={onClose}
        onOpen={onOpen}
        open={open}
        filterOptions={filterOptions}
        renderOption={renderOptionFunc}
        renderInput={renderInput || renderInputFunc}
        renderTags={renderer}
        ListboxComponent={shouldVirtualize ? VirtualizedListbox : undefined}
        disableListWrap={shouldVirtualize}
        componentsProps={compProps}
        blurOnSelect={blurOnSelect}
        clearOnBlur={clearOnBlur}
        noOptionsText={noOptionsText}
        freeSolo={freeSolo}
      />
      {loading && <S.LoadingIcon size={16} color="var(--button-loading-icon-color)" rotate />}
    </S.AutocompleteWrapper>
  );
};

Autocomplete.propTypes = {
  label: PropTypes.string,
  testId: PropTypes.string,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  multiple: PropTypes.bool,
  clearable: PropTypes.bool,
  width: PropTypes.string,
  height: PropTypes.oneOfType([
    PropTypes.oneOf(['LARGE', 'MEDIUM', 'SMALL', 'TINY', 'DYNAMIC']),
    PropTypes.string,
    PropTypes.number
  ]),
  minHeight: PropTypes.oneOfType([
    PropTypes.oneOf(['LARGE', 'MEDIUM', 'SMALL', 'TINY']),
    PropTypes.string,
    PropTypes.number
  ]),
  maxPopperHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onInputChange: PropTypes.func,
  inputValue: PropTypes.string,
  onClose: PropTypes.func,
  opOpen: PropTypes.func,
  open: PropTypes.bool,
  renderValue: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf(['CHIPS', 'COMMA_SEPARATED', 'COUNT'])]),
  chipsCount: PropTypes.number,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      disabled: PropTypes.bool,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      icon: PropTypes.oneOfType([PropTypes.node, PropTypes.element]),
      dotColor: PropTypes.string,
      children: PropTypes.array
    })
  ).isRequired,
  disablePortal: PropTypes.bool,
  isOptionEqualToValue: PropTypes.func,
  filterOptions: PropTypes.func,
  clearOnBlur: PropTypes.bool,
  blurOnSelect: PropTypes.bool,
  noOptionsText: PropTypes.string,
  renderInput: PropTypes.func,
  freeSolo: PropTypes.bool,
  readOnly: PropTypes.bool,
  loading: PropTypes.bool,
  mode: PropTypes.oneOf([Themes.DARK, Themes.LIGHT]),
  paperSx: PropTypes.shape({}),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  autoFocus: PropTypes.bool,
  virtualize: PropTypes.bool
};

export default Autocomplete;
